import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../components/PaymentActions';

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
const newActionId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const STATUS_BADGE = {
  SUCCESS: 'bg-green-100 text-green-800',
  QUEUED: 'bg-amber-100 text-amber-800',
  PENDING: 'bg-amber-100 text-amber-800',
  ERROR: 'bg-red-100 text-red-800',
};
const StatusBadge = ({ s }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[s] || 'bg-gray-100 text-gray-700'}`}>
    {s || 'unknown'}
  </span>
);

const AccountFlag = ({ flag }) =>
  flag ? <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800" title="Account is on hold / suspended">{flag === 'suspended' ? 'Suspended' : 'On hold'}</span> : null;

const KpiCard = ({ title, value, sub, tone }) => (
  <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${tone}`}>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    {sub != null && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

// Complete a pending refund now that Shippo confirmed the void (SUCCESS). Reuses the
// refund-with-void action: with a refund id already on the order it re-checks Shippo and
// issues the Stripe refund. Amount defaults to the FULL refundable balance if left blank —
// a cancellation we initiate refunds 100% (the 3% fee applies only to customer self-cancels).
const CompleteModal = ({ item, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionId] = useState(newActionId);
  const ready = typed.trim().toUpperCase() === 'CONFIRM';

  const submit = async () => {
    setBusy(true);
    try {
      const body = { reason, actionId };
      if (amount !== '' && Number(amount) > 0) body.amount = Number(amount);
      const res = await adminApi.refundWithVoid(item.orderId, body);
      // Interceptor returns the response body directly; distinguish by shape, not HTTP status.
      if (res && res.success && res.amountRefundedUsd != null) {
        toast.success(`Refunded ${usd(res.amountRefundedUsd)} for ${item.orderId}${res.customerNotified ? ' · customer emailed' : ' · email not sent'}`);
      } else {
        toast.info(res?.message || 'Still pending at Shippo — no money moved yet.');
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Complete refund failed');
      onDone(); // reload — the order may have terminalized server-side (e.g. ERROR persisted)
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-gray-600 bg-opacity-75 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mt-10 sm:mt-20 p-5">
        <h3 className="text-lg font-bold text-gray-900">Complete refund — {item.orderId}</h3>
        {item.email && <p className="text-sm text-gray-500 mt-0.5 break-all">{item.email}</p>}
        <AccountFlag flag={item.accountFlag} />
        <p className="text-xs text-gray-400 mt-2">
          Paid {usd(item.amountPaidUsd)} · Shippo void <strong>{item.shippoRefundStatus || item.refundStatus || '—'}</strong> ·
          opened {item.daysPending != null ? `${item.daysPending}d ago` : '—'}{item.initiatedBy ? ` by ${item.initiatedBy}` : ''}
        </p>
        <div className="mt-3 rounded-md bg-green-50 border border-green-200 p-3 text-xs text-green-800">
          Shippo has confirmed the label void. This issues the customer's Stripe refund now (idempotent).
        </div>

        <label className="block mt-4 text-sm">
          <span className="text-gray-700">Refund amount (USD)</span>
          <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="Leave blank = full refund"
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]" />
          <span className="text-xs text-gray-400">Blank refunds the full refundable balance (no fee deducted on a cancellation we initiate).</span>
        </label>
        <label className="block mt-3 text-sm">
          <span className="text-gray-700">Reason</span>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]">
            <option value="requested_by_customer">Requested by customer</option>
            <option value="duplicate">Duplicate</option>
            <option value="fraudulent">Fraudulent</option>
          </select>
        </label>
        <label className="block mt-3 text-sm">
          <span className="text-gray-700">Type <strong>CONFIRM</strong> to authorize this refund</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="CONFIRM"
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px] uppercase" />
        </label>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={busy}
            className="min-h-[44px] px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={!ready || busy}
            className="min-h-[44px] px-4 py-2 rounded-md text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-40">
            {busy ? 'Working…' : 'Complete refund'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PendingRefunds = () => {
  const { user } = useAuth();
  const role = user?.role || 'viewer';
  const isFinance = can(role, 'finance');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalItem, setModalItem] = useState(null);
  const [acking, setAcking] = useState(null);

  const load = useCallback(async (live = true) => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.getPendingRefunds({ live: live ? 1 : 0 });
      setData(res);
    } catch (e) {
      const msg = e.response?.status === 403
        ? 'This worklist requires the finance role.'
        : (e.response?.data?.message || e.message || 'Failed to load pending refunds');
      setError(msg);
      if (e.response?.status !== 403) toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isFinance) load(true); else setError('This worklist requires the finance role.'); }, [load, isFinance]);

  const items = data?.items || [];
  const onDone = () => { setModalItem(null); load(true); };

  // Acknowledge a Shippo ERROR (label was used → not refundable). refund-with-void records
  // ERROR server-side and rejects with the reason; reloading then drops the row off the list.
  const acknowledgeError = async (it) => {
    setAcking(it.orderId);
    try {
      await adminApi.refundWithVoid(it.orderId, { reason: 'requested_by_customer', actionId: newActionId() });
      toast.success(`${it.orderId} resolved`);
    } catch (e) {
      toast.info(e.response?.data?.message || 'Shippo ERROR recorded — label was used, not refundable.');
    } finally { setAcking(null); load(true); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refunds — Pending at Shippo</h1>
          <p className="text-sm text-gray-500">Label voids in flight (QUEUED/PENDING) awaiting Shippo confirmation, then complete the customer refund · your role: <span className="font-medium">{role}</span></p>
        </div>
        <button onClick={() => load(true)} disabled={loading || !isFinance}
          className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          <ArrowPathIcon className={`h-5 w-5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh (live Shippo)
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <KpiCard title="Pending" value={data.count || 0} sub="voids awaiting Shippo" tone="border-amber-400" />
          <KpiCard title="Ready to complete" value={data.readyToComplete || 0} sub="Shippo SUCCESS — refund now" tone="border-green-500" />
          <KpiCard title="Stuck > 21 days" value={data.stuck || 0} sub="investigate / escalate" tone="border-red-500" />
          <KpiCard title="Shippo ERROR" value={data.errored || 0} sub="label used — not refundable" tone="border-red-500" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-4 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" /><span className="text-sm">{error}</span>
        </div>
      )}

      {loading && !data && <div className="text-center text-gray-500 py-12">Loading…</div>}

      {data && items.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500">
          ✅ No pending refunds — every initiated void has settled.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Order', 'Customer', 'Paid', 'Opened', 'Shippo refund', 'Action'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => (
                  <tr key={it.orderId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{it.orderId}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 break-all">{it.email}<AccountFlag flag={it.accountFlag} /></td>
                    <td className="px-4 py-3 text-sm text-gray-900">{usd(it.amountPaidUsd)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {it.daysPending != null ? `${it.daysPending}d ago` : '—'}
                      {it.stuck && <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">STUCK</span>}
                      {it.initiatedBy && <div className="text-xs text-gray-400">by {it.initiatedBy}</div>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge s={it.shippoRefundStatus || it.refundStatus} />{!it.isStripe && <div className="text-xs text-gray-400 mt-0.5">non-Stripe</div>}</td>
                    <td className="px-4 py-3">
                      {it.readyToComplete && isFinance ? (
                        <button onClick={() => setModalItem(it)} className="min-h-[40px] px-3 py-1.5 rounded text-xs font-semibold text-white bg-green-600 hover:bg-green-700">Complete refund</button>
                      ) : it.errored && isFinance ? (
                        <button onClick={() => acknowledgeError(it)} disabled={acking === it.orderId} className="min-h-[40px] px-3 py-1.5 rounded text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{acking === it.orderId ? 'Working…' : 'Acknowledge error'}</button>
                      ) : (
                        <span className="text-xs text-gray-400">Awaiting Shippo</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {items.map((it) => (
              <div key={it.orderId} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{it.orderId}</span>
                  <StatusBadge s={it.shippoRefundStatus || it.refundStatus} />
                </div>
                <p className="text-sm text-gray-600 break-all mt-1">{it.email}<AccountFlag flag={it.accountFlag} /></p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div><span className="text-gray-400">Paid</span><br />{usd(it.amountPaidUsd)}</div>
                  <div><span className="text-gray-400">Opened</span><br />{it.daysPending != null ? `${it.daysPending}d ago` : '—'}{it.stuck ? ' · STUCK' : ''}</div>
                </div>
                {it.readyToComplete && isFinance && (
                  <div className="mt-3"><button onClick={() => setModalItem(it)} className="w-full min-h-[44px] px-3 py-2 rounded text-sm font-semibold text-white bg-green-600 hover:bg-green-700">Complete refund</button></div>
                )}
                {it.errored && isFinance && (
                  <div className="mt-3"><button onClick={() => acknowledgeError(it)} disabled={acking === it.orderId} className="w-full min-h-[44px] px-3 py-2 rounded text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{acking === it.orderId ? 'Working…' : 'Acknowledge error'}</button></div>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            "Refresh (live Shippo)" re-polls each refund's current status. Complete a refund only once Shippo reports SUCCESS; refunds stuck &gt; {data?.abandonDays || 21} days should be investigated.
          </p>
        </>
      )}

      {modalItem && <CompleteModal item={modalItem} onClose={() => setModalItem(null)} onDone={onDone} />}
    </div>
  );
};

export default PendingRefunds;
